
import axios from 'axios';
import querystring from 'querystring';
import { getIn, genGuid, channel } from '../utils';
import moment from 'moment';

const API_URL = '';

function handleError(e) {
  if (e.response && e.response.data) {
    e.backendMessage = e.response.data.Message;
  }
  throw e;
}

function watchedApi(target, name, descriptor) {
  const original = descriptor.value;
  descriptor.value = function (...args) {
    this.chnRequestStarted.trigger(args);
    return original.apply(this, args)
      .then(result => {
        this.chnRequestFulfilled.trigger(result);
        this.chnRequestEnded.trigger(result);
        return result;
      })
      .catch(error => {
        this.chnRequestRejected.trigger(error);
        this.chnRequestEnded.trigger(error);
        throw error;
      })

  }
}

export class Api {
  chnRequestStarted = channel();
  chnRequestFulfilled = channel();
  chnRequestRejected = channel();
  chnRequestEnded = channel();

  constructor(flasher) {
    let reqRunning = 0;
    let anyCommRunning = false;
    let timeoutId;
    this.chnRequestStarted.subscribe(() => {
      if (reqRunning === 0) {
        if (!anyCommRunning) {
          console.log('### STARTING COMMUNICATION.')
          flasher.showSpinner(true);
          anyCommRunning = true;
        }
        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
          timeoutId = undefined;
        }
      }
      reqRunning++;
    });
    this.chnRequestEnded.subscribe(() => {
      if (reqRunning === 1) {
        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
          timeoutId = undefined;
        }
        timeoutId = setTimeout(() => {
          console.log('### ALL COMMUNICATION FINISHED.');
          flasher.showSpinner(false);
          anyCommRunning = false;
          timeoutId = undefined;
        }, 50)
      }
      reqRunning--;
    });
  }

  @watchedApi
  getRentalObject({ organizationId, objectId }) {
    return axios.get(`${API_URL}/api/public/RentalObject/${objectId}/json`, {
      params: { organizationId },
    })
      .then(response => ({
        ...(response.data.ROOT.RentalObject[0] || {}),
        BusinessUnit: (response.data.ROOT.BusinessUnit[0] || {}),
        Seasons: (response.data.ROOT.Season || []),
        PickupSchedule: (response.data.ROOT.PickupSchedule || []),
        DropoffSchedule: (response.data.ROOT.DropoffSchedule || []),
      }))
      .catch(error => handleError(error));
  }

  @watchedApi
  getBestRentalTransactionOffers({ startDate, endDate, organizationId, objectId, granularityTimeUnitId, oldStartDate, oldEndDate }) {
    return axios.get(`${API_URL}/api/public/RentalObject/${objectId}/bestRentalTransactionOffers`, {
      params: { startDate, endDate, organizationId, granularityTimeUnitId, oldStartDate, oldEndDate },
    })
      .then(response => response.data.ROOT)
      .catch(error => handleError(error));
  }

  @watchedApi
  getTimelineAllotmentWithSeasons({ startDate, endDate, organizationId, ids, durationTimeUnitId, durationTimeUnitQuantity }) {
    return axios.get(`${API_URL}/api/public/RentalObject/getTimelineAllotmentWithSeasons`, {
      params: { startDate, endDate, organizationId, durationTimeUnitId, durationTimeUnitQuantity, ids },
    })
      .then(response => response.data.ROOT.RentalObject[0].RentalObjectBookableAllotment)
      .catch(error => handleError(error));
  }

  @watchedApi
  getUnavailablePickupAndDropoffTimes({ startDate, endDate, organizationId, objectId }) {
    return axios.get(`${API_URL}/api/public/RentalObject/${objectId}/getUnavailablePickupAndDropoffTimes`, {
      params: { startDate, endDate, organizationId },
    })
      .then(response => (response.data.ROOT.RentalObject[0] || {}).Day)
      .catch(error => handleError(error));
  }

  @watchedApi
  getSearchResults({ rentalCategoryId, organizationId, rentalSubcategoryId, businessUnitId, StartDate, EndDate, pageNumber, pageSize }) {
    return axios.get(`${API_URL}/api/public/SearchObjects`, {
      params: { rentalCategoryId, organizationId, rentalSubcategoryId, businessUnitId, StartDate, EndDate, pageNumber, pageSize },
    })
      .then(response => getIn(response.data.ROOT, ['RentalObject'], []))
      .catch(error => handleError(error));
  }

  @watchedApi
  getBusinessUnit({ organizationId, businessUnitId }) {
    return axios.get(`${API_URL}/api/public/BusinessUnit/${businessUnitId}/json`, {
      params: { organizationId }
    })
      .then(response => response.data.ROOT.BusinessUnit[0])
      .catch(error => handleError(error));
  }

  @watchedApi
  getOrgSettings({ orgCode }) {
    return axios.get(`/clients/standard/release/assets/portals/${orgCode}/settings.json`)
      .then(response => {
        return response.data;
      })
      .catch(err => {
        return {};
      });
  }

  @watchedApi
  getOrganization({ organizationId }) {
    let startDate = moment();
    return axios.get(`${API_URL}/api/public/Organization/${organizationId}/json`)
      .then(response => {
        let timeUnits = getIn(response.data, ['ROOT', 'Organization', 0, 'OrganizationDurationAllotmentTimeUnit'], []);
        return Promise.all(
          timeUnits.map(atu => {
            return this.getDateTimeIntervalFromDuration({
              durationTimeUnitId: atu.refAllotmentTimeUnitId,
              startDate: startDate.toISOString(),
              durationTimeUnitQuantity: 1,
            })
          })
        )
          .then(intervals => {
            for (let i = 0; i < intervals.length; i++) {
              let durationSecs = moment(intervals[i].endDate).diff(moment(intervals[i].startDate), 'seconds');
              timeUnits[i].timeLengthSec = durationSecs;
            }
            return response;
          })
      })
      .then(response => {
        var orgCode = response.data.ROOT.Organization[0].ReferenceCode.toLowerCase();
        return this.getOrgSettings({ orgCode }).then(settings => {
          response.data.ROOT.Organization[0].settings = settings;
          return response;
        })
      })
      .then(response => {
        return this.getMinPickupTime({ organizationId }).then(fromTimeHour => {
          response.data.ROOT.Organization[0].minPickupTime = fromTimeHour.FromTime;
          //response.data.ROOT.Organization[0].fromHour = fromTimeHour.FromHour;
          return response;
        })
      })
      .then(response => {
        return response.data.ROOT.Organization[0]
      })
      .catch(error => handleError(error));
  }

  @watchedApi
  getSectionsWithRentalObjects({ organizationId }) {
    return axios.get(`${API_URL}/api/public/Organization/${organizationId}/getSectionsWithRentalObjects`)
      .then(response => response.data.ROOT.RentalCategory)
      .catch(error => handleError(error));
  }

  @watchedApi
  getMinPickupTime({ organizationId }) {
    return axios.get(`${API_URL}/api/public/Organization/${organizationId}/getMinPickupTime`)
      .then(response => response.data.ROOT.SchedulePeriod[0])
      .catch(error => handleError(error));
  }

  @watchedApi
  getMasterData() {
    return Promise.all([
      axios.get(`${API_URL}/api/public/Salutations`).then(response => response.data.ROOT.Salutation),
      axios.get(`${API_URL}/api/public/Countries`).then(response => response.data.ROOT.Country),
      axios.get(`${API_URL}/api/public/TimeZones`).then(response => response.data.ROOT.TimeZone),
      axios.get(`${API_URL}/api/public/AddressTypes`).then(response => response.data.ROOT.AddressType),
      axios.get(`${API_URL}/api/public/Languages`).then(response => response.data.ROOT.Language),
    ])
      .then(([salutations, countries, timeZones, addressTypes, languages]) =>
        ({ salutations, countries, timeZones, addressTypes, languages }))
      .catch(error => handleError(error));
  }

  @watchedApi
  getDateTimeIntervalFromDuration({ durationTimeUnitId, startDate, durationTimeUnitQuantity }) {
    return axios.get(`${API_URL}/api/public/DateTimeIntervalFromDuration`, {
      params: {
        durationTimeUnitId,
        startDate,
        durationTimeUnitQuantity,
      },
    })
      .then(response => ({
        startDate: response.data.ROOT.DateTimeInterval[0].StartDate,
        endDate: response.data.ROOT.DateTimeInterval[0].EndDate,
      }))
      .catch(error => handleError(error));
  }

  @watchedApi
  getRentalTransaction({ transactionId }) {
    return axios.get(`${API_URL}/api/public/RentalTransaction/${transactionId}`)
      .then(response => response.data.ROOT)
      .catch(error => handleError(error));
  }

  @watchedApi
  updateRentalTransaction({ organizationId, duration, startDate, transactionId, objectId, timeUnitId }) {
    return axios({
      url: `${API_URL}/api/public/RentalTransaction/Update`,
      method: 'post',
      params: {
        organizationId, duration, startDate, transactionId, objectId, timeUnitId
      }
    })
      .then(response => response.data.ROOT)
      .catch(error => handleError(error));
  }

  @watchedApi
  deleteRentalTransactionOffer({ organizationId, transactionId, deleter }) {
    return axios({
      url: `${API_URL}/api/public/RentalTransaction/${transactionId}/DeleteRentalTransactionOffer`,
      method: 'post',
      params: {
        organizationId,
      },
      data: {
        ROOT: deleter,
      }
    })
      .then(response => response.data.ROOT)
      .catch(error => handleError(error));
  }

  @watchedApi
  updateRentalTransactionOffers({ organizationId, transactionId, updater }) {
    return axios({
      url: `${API_URL}/api/public/RentalTransaction/${transactionId}/UpdateRentalTransactionOffers`,
      method: 'post',
      params: {
        organizationId
      },
      data: {
        ROOT: updater,
      }
    })
      .then(response => response.data.ROOT)
      .catch(error => handleError(error));
  }

  @watchedApi
  addRentalService({ organizationId, transactionId, rentalServiceId, rentalTransactionAccessoryId }) {
    return axios({
      url: `${API_URL}/api/public/RentalTransaction/${transactionId}/AddRentalService`,
      method: 'post',
      params: {
        organizationId, rentalServiceId, rentalTransactionAccessoryId
      }
    })
      .then(response => response.data.ROOT)
      .catch(error => handleError(error));
  }

  @watchedApi
  removeRentalService({ organizationId, transactionId, rentalTransactionServiceId }) {
    return axios({
      url: `${API_URL}/api/public/RentalTransaction/${transactionId}/RemoveRentalService`,
      method: 'post',
      params: {
        organizationId, rentalTransactionServiceId
      }
    })
      .then(response => response.data.ROOT)
      .catch(error => handleError(error));
  }

  @watchedApi
  setAccessoryQuantity({ transactionId, quantity, rentalTransactionAccessoryId }) {
    return axios({
      url: `${API_URL}/api/public/RentalTransaction/${transactionId}/ChangeAccessoryQuantity`,
      method: 'post',
      params: {
        quantity,
        rentalTransactionAccessoryId,
      }
    }).then(response => response.data.ROOT)
      .catch(error => handleError(error));
  }

  @watchedApi
  login({ username, password }) {
    return axios.request({
      url: `${API_URL}/AjaxLogin`,
      data: JSON.stringify({
        username,
        password,
      }),
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
      }
    }).then(response => response.data)
      .catch(error => handleError(error));
  }

  // @watchedApi
  // logout() {
  //   return axios.request({
  //     url: `${API_URL}/AjaxSignout`,
  //   })
  //     .catch(error => handleError(error));
  // }

  @watchedApi
  getAuthenticatedUser(organizationId) {
    let url = `${API_URL}/api/public/GetAuthenticatedUser`;
    if (organizationId) {
      url += `?organizationId=${organizationId}`;
    }
    return axios.get(url)
      .then(response => getIn(response.data.ROOT, ['OrganizationBusinessPartner', 0]))
      .catch(error => handleError(error));
  }

  @watchedApi
  getPersonalData(organizationId, lesseeId) {
    return axios.get(`${API_URL}/api/PersonalData?organizationId=${organizationId}&lesseeId=${lesseeId}`)
      .then(response => response.data.ROOT)
      .catch(error => handleError(error));
  }

  @watchedApi
  updatePersonalData({ organizationId, lesseeId, profileStructure }) {
    return axios.request({
      url: `${API_URL}/api/PersonalData`,
      method: 'put',
      params: {
        organizationId,
        lesseeId
      },      
      data: profileStructure,
    })
      .catch(error => handleError(error));
  }

  @watchedApi
  changePassword({ profileStructure }) {
    return axios.request({
      url: `${API_URL}/api/Lessee/ChangePassword`,
      method: 'post',
      data: profileStructure,
    })
      .catch(error => handleError(error));
  }

  @watchedApi
  createNewLessee({ organizationId, profileStructure }) {
    return axios.request({
      url: `${API_URL}/api/public/CreateNewLessee`,
      method: 'post',
      params: {
        lessorOrganizationId: organizationId,
      },
      data: profileStructure,
    })
      .catch(error => handleError(error));
  }

  @watchedApi
  confirmUserEmail({ userId, token }) {
    return axios.request({
      url: `${API_URL}/confirmUserEmail.json`,
      params: {
        userId,
        token,
      }
    })
      .then(response => response.data)
      .catch(error => handleError(error));
  }

  makeAddress({ AddressName, AddressAddition, City, PostalCode, Street, refAddressTypeId, refCountryId }) {
    return function ({ Id }) {
      return {
        AddressName,
        AddressAddition,
        City,
        PostalCode,
        Street,
        refAddressTypeId,
        refCountryId,
        Id,
      }
    }
  }

  makeOBP({ Email, FirstName, Name, Phone, Mobile, refSalutationId }) {
    return function ({ Id, refBusinessUnitId, refOrganizationId }) {
      return {
        Email,
        FirstName,
        Name,
        Phone,
        Mobile,
        refSalutationId,
        Id,
        refBusinessUnitId,
        refOrganizationId,
      }
    }
  }

  @watchedApi
  setGuestData({ organizationId, transactionId, address1, address2, address3, obp }) {
    const idAddress1 = genGuid();
    const idAddress2 = genGuid();
    const idAddress3 = genGuid();
    const newOrgId = genGuid();
    const newBUId = genGuid();
    const idOBP = genGuid();
    const data = {
      ROOT: {
        Organization: {
          Id: newOrgId,
          BusinessUnit: {
            Id: newBUId,
            BusinessUnitAddress: [
              address1 && address1({ Id: idAddress1 }),
              address2 && address2({ Id: idAddress2 }),
              address3 && address3({ Id: idAddress3 }),
            ].filter(o => o)
          },
          OrganizationBusinessPartner: obp({
            Id: idOBP,
            refBusinessUnitId: newBUId,
            refOrganizationId: newOrgId,
          })
        }
      }
    }
    return axios({
      url: `${API_URL}/api/public/RentalTransaction/${transactionId}/SetGuestData`,
      method: 'post',
      params: {
        organizationId,
      },
      data,
    })
      .then(response => response.data.ROOT)
      .catch(error => handleError(error));
  }

  @watchedApi
  setCurrentUserAsLessee({ organizationId, transactionId }) {
    return axios.get(`${API_URL}/api/public/RentalTransaction/${transactionId}/SetCurrentUserAsALessee`,
      {
        params: {
          organizationId
        }
      })
      .then(response => response.data.ROOT)
      .catch(error => handleError(error));
  }

  @watchedApi
  reserve({ organizationId, transactionId, bookingNote, bookingNote2, paymentMethodId }) {
    return axios({
      url: `${API_URL}/api/public/RentalTransaction/${transactionId}/Reserve`,
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      method: 'post',
      data: querystring.stringify({
        organizationId, paymentMethodId, bookingNote, bookingNote2
      })
    })
      .then(response => response.data.ROOT)
      .catch(error => handleError(error));
  }

  //===========================================================

  @watchedApi
  book({ organizationId, transactionId, bookingNote, bookingNote2, paymentMethodId }) {
    return axios({
      url: `${API_URL}/api/public/RentalTransaction/${transactionId}/Book`,
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      method: 'post',
      data: querystring.stringify({
        organizationId, paymentMethodId, bookingNote, bookingNote2, dataTransReturnLocation: 'LesseeWizardSTD4'
      })
    })
      .then(response => response.data.ROOT)
      .catch(error => handleError(error));
  }

  @watchedApi
  confirmSelection({ organizationId, transactionId }) {
    return axios({
      url: `${API_URL}/api/public/RentalTransaction/${transactionId}/ConfirmSelection`,
      method: 'post',
      params: {
        organizationId,
      }
    })
      .then(response => response.data.ROOT)
      .catch(error => handleError(error));
  }

  @watchedApi
  getConfirmation({ trxId }) {
    return axios({
      url: `${API_URL}/api/public/RentalTransaction/${trxId}/Confirmation`,
      method: 'get',
    })
      .then(response => response.data.ROOT)
      .catch(error => handleError(error));
  }

  @watchedApi
  getRecentBookings({ startDate, endDate, pageSize, pageNumber, isActive }) {
    return axios({
      url: `${API_URL}/api/Lessee/RentalTransaction/ListMy${isActive ? 'Active' : ''}`,
      method: 'get',
      params: { startDate, endDate, pageSize, pageNumber },
    })
      .then(response => response.data.ROOT.RentalTransaction)
      .catch(error => handleError(error));
  }

  @watchedApi
  getRecentBookingDetail({ transactionId }) {
    return axios({
      url: `${API_URL}/api/Lessee/RentalTransaction/${transactionId}`,
      method: 'get',
    })
      .then(response => response.data.ROOT.RentalTransaction[0])
      .catch(error => handleError(error));
  }

  @watchedApi
  cancelBooking({ trxId, ffStatusCancelId }) {
    const data = {
      ROOT: {
        RentalTransaction: [
          {
            Id: trxId,
            refRentalTransactionFulfillmentStatusId: ffStatusCancelId
          }
        ]
      }
    };
    return axios({
      url: `${API_URL}/api/Lessee/RentalTransaction/${trxId}/Edit`,
      method: 'post',
      data
    })
      .then(response => response.data.ROOT.RentalTransaction[0])
      .catch(error => handleError(error));
  }

  @watchedApi
  getCancellationFee({ trxId }) {
    return axios({
      url: `${API_URL}/api/Lessee/RentalTransaction/${trxId}/computeCancellationFee`,
      method: 'post',
    })
      .then(response => {
        return response.data.ROOT;
      })
      .catch(error => handleError(error));
  }

  @watchedApi
  editTransaction({ trxId, body }) {
    return axios({
      url: `${API_URL}/api/public/RentalTransaction/${trxId}/EditCustomerDetails`,
      method: 'post',
      data: body
    })
      .then(response => response)
      .catch(error => handleError(error));
  }

  editAddresses(trxId, addresses) {
    const data = {
      ROOT: {
        RentalTransaction: [
          {
            Id: trxId,
            RentalTransactionCustomerAddress: addresses.map(o => {
              return o.values;
            })
          },
        ],
      },
    };
    return this.editTransaction({ trxId, body: data })
  }


  @watchedApi
  invokeResetPassword({ organizationId, email }) {
    return axios({
      url: `${API_URL}/api/public/InvokeResetPassword`,
      method: 'post',
      data: {
        email,
        organizationId,
      }
    })
      .then(response => response)
      .catch(error => handleError(error));
  }

  @watchedApi
  resetPassword({ newPassword, confirmPassword, token, userName }) {
    return axios({
      url: `${API_URL}/api/public/ResetPassword`,
      method: 'post',
      data: {
        newPassword,
        confirmPassword,
        token,
        username: userName,
      }
    })
      .then(response => response)
      .catch(error => handleError(error));
  }

  @watchedApi
  getIdByReferenceCode({ code }) {
    return axios({
      url: `${API_URL}/api/public/Organization/getIdByReferenceCode`,
      method: 'get',
      params: { referenceCode: code },
    })
      .then(response => response.data.ROOT.Organization[0])
      .catch(error => handleError(error));
  }

  @watchedApi
  checkVoucherCode({ trxId, code }) {
    return axios({
      url: `${API_URL}/api/public/RentalTransaction/${trxId}/CheckVoucher`,
      method: 'get',
      params: { code },
    })
      .then(response => response.data.ROOT)
      .catch(error => handleError(error));
  }

}


// WEBPACK FOOTER //
// ./src/api/index.js